import { drawing as d } from '@progress/kendo-drawing';
import { VisualBase } from './VisualBase';
import { defined } from './../utils';
import { AutoSizeableMixin } from './AutoSizeableMixin';
import { MarkerPathMixin } from './MarkerPathMixin';
import { deepExtend } from './../utils/deep-extend';
/**
 * Represents an SVG path element with auto-sizing and marker capabilities.
 * Combines VisualBase with AutoSizeableMixin and MarkerPathMixin functionality.
 */
export class Path extends VisualBase {
    /**
     * Creates a new Path instance.
     * @param options - Configuration options for the path
     */
    constructor(options) {
        options = deepExtend({ autoSize: true }, options);
        super(options);
        /** @hidden */
        this._capMap = MarkerPathMixin._capMap;
        this.container = new d.Group();
        this._setScale = AutoSizeableMixin._setScale.bind(this);
        this._setTranslate = AutoSizeableMixin._setTranslate.bind(this);
        this._initSize = AutoSizeableMixin._initSize.bind(this);
        this._updateSize = AutoSizeableMixin._updateSize.bind(this);
        this._getPath = MarkerPathMixin._getPath.bind(this);
        this._normalizeMarkerOptions = MarkerPathMixin._normalizeMarkerOptions.bind(this);
        this._removeMarker = MarkerPathMixin._removeMarker.bind(this);
        this._createMarkers = MarkerPathMixin._createMarkers.bind(this);
        this._createMarker = MarkerPathMixin._createMarker.bind(this);
        this._positionMarker = MarkerPathMixin._positionMarker.bind(this);
        this._redrawMarker = MarkerPathMixin._redrawMarker.bind(this);
        this._redrawMarkers = MarkerPathMixin._redrawMarkers.bind(this);
        this._createElements();
        this._initSize();
    }
    /**
     * Gets the drawing container that holds the path and its markers.
     * @returns The drawing group container
     */
    drawingContainer() {
        return this.container;
    }
    /**
     * Gets or sets the path data string for the SVG path.
     * @param value The SVG path data string to set, or undefined to get current data
     * @returns The current path data string when called as getter
     */
    data(value) {
        const options = this.options;
        if (value) {
            if (options.data !== value) {
                options.data = value;
                this._setData(value);
                this._initSize();
                this._redrawMarkers(true, {});
            }
        }
        else {
            return options.data;
        }
    }
    /**
     * Redraws the path with updated options.
     * @param options - Updated configuration options
     */
    redraw(options) {
        if (options) {
            super.redraw(options);
            const pathOptions = this.options;
            const data = options.data;
            if (defined(data) && pathOptions.data !== data) {
                pathOptions.data = data;
                this._setData(data);
                if (!this._updateSize(options)) {
                    this._initSize();
                }
                this._redrawMarkers(true, options);
            }
            else {
                this._updateSize(options);
                this._redrawMarkers(false, options);
            }
        }
    }
    /**
     * @hidden
     * Creates the drawing elements for the path.
     */
    _createElements() {
        const options = this.options;
        this.drawingElement = d.Path.parse(options.data || '', {
            stroke: options.stroke
        });
        this._fill();
        this.container.append(this.drawingElement);
        this._createMarkers();
    }
    /**
     * @hidden
     * Sets the path data for the drawing element.
     * @param data - The SVG path data string
     */
    _setData(data) {
        const drawingElement = this.drawingElement;
        const multipath = d.Path.parse(data || '');
        const paths = multipath.paths.slice(0);
        multipath.paths.elements([]);
        drawingElement.paths.elements(paths);
    }
}
